import React, { useState, useEffect } from 'react';
import { doc, collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';

// --- GAME CONSTANTS NEEDED FOR THIS PAGE ---
const MASTER_ACCOUNT_WAX_ID = 'whatifreward';

const tokenContracts = {
    'WAX': 'eosio.token', 'WHATIF': 'yoyinsoly214', 'FIREMAN': 'yoyinsoly214',
    'PUZZL': 'yoyinsoly214', 'WTFWUF': 'yoyinsoly214', 'WTFBILL': 'yoyinsoly214',
    'WHATIFX': 'yoyinsoly214',
    'TESTERU': 'yoyinsoly214', 'XMASZS': 'yoyinsoly214',
};

const tokenDecimals = {
    'WAX': 8, 'WHATIF': 8, 'FIREMAN': 5, 'PUZZL': 8, 'WTFWUF': 8, 'WTFBILL': 8,
    'WHATIFX': 8, 'TESTERU': 8, 'XMASZS': 6,
};

const buildingConfigClient = {
    'forge': { name: 'Forge', boostType: 'MP Boost', boostPerLevel: 2.5, upgradeToken: 'FIREMAN', description: 'Increases the MP of all gang members.' },
    'workshop': { name: 'Workshop', boostType: 'Speed Boost', boostPerLevel: 2.5, upgradeToken: 'WTFWUF', description: 'Boosts the Speed of all members.' },
    'barracks': { name: 'Barracks', boostType: 'Stamina Boost', boostPerLevel: 5, upgradeToken: 'WHATIF', description: 'Increases the maximum daily Stamina for all members.' },
    'bastion': { name: 'Bastion', boostType: 'Base MP Boost', boostPerLevel: 2.5, upgradeToken: 'PUZZL', description: 'Increases the Base MP of all gang members.' },
    'observatory': { name: 'Observatory', boostType: 'Points Gain', boostPerLevel: 2.5, upgradeToken: 'WTFBILL', description: 'Increases the Daily & Seasonal Points Dungeon Runs.' },
    'scribeLibrary': { name: 'Scribe\'s Library', boostType: 'XP Gain', boostPerLevel: 2.5, upgradeToken: 'XMASZS', description: 'Speeds up player leveling for all gang members.' },
    'altarOfFortune': { name: 'Altar of Fortune', boostType: 'NFT Gain Chance', boostPerLevel: 0.5, upgradeToken: 'WHATIFX', description: 'Increases the chance to win a pack in the daily rewards.' },
    'watchtower': { name: 'Watchtower', boostType: 'Vault APR Boost', boostPerLevel: 0.25, upgradeToken: 'TESTERU', description: 'Enhances token rewards from the WHATIF Vault.' }
};

const getUpgradeCostClient = (level, token) => {
    const baseCost = {
        'FIREMAN': 50000, 'WTFWUF': 10000, 'WHATIF': 2000000,
        'PUZZL': 1800, 'WTFBILL': 2, 'XMASZS': 10,
        'WHATIFX': 500, 'TESTERU': 4000
    };
    if (!baseCost[token]) return Infinity;
    return baseCost[token] * Math.pow(2, level);
};

const getUpgradeCooldownHoursClient = (level) => {
    const baseCooldown = 24;
    const maxCooldown = 16 * 24;
    const calculatedCooldown = baseCooldown * Math.pow(2, level);
    return Math.min(calculatedCooldown, maxCooldown);
};

// --- COMPONENT START ---
function GangsPage({ session, playerData, createGangCallable, joinGangCallable, leaveGangCallable, upgradeGangBuildingCallable, depositToGangTreasuryCallable, VAULT_TOKEN_VALUE, activateGangPassCallable, gangPassNfts }) {
    const [gangName, setGangName] = useState('');
    const [gangTag, setGangTag] = useState('');
    const [feedback, setFeedback] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isRaiding, setIsRaiding] = useState(false);
    const [gangData, setGangData] = useState(null);
    const [allGangs, setAllGangs] = useState([]);
    const [members, setMembers] = useState([]);
    const [depositAmount, setDepositAmount] = useState('');
    const [depositTokenSymbol, setDepositTokenSymbol] = useState('FIREMAN');
    const [timeRemaining, setTimeRemaining] = useState({});
    const [selectedGangPass, setSelectedGangPass] = useState('');

    // Listener for all gangs (for the join list)
    useEffect(() => {
        const gangsQuery = query(collection(db, "gangs"), orderBy("memberCount", "desc"));
        const unsubscribe = onSnapshot(gangsQuery, (snapshot) => {
            const gangsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAllGangs(gangsList);
        });
        return () => unsubscribe();
    }, []);

    // Listener for the player's specific gang data
    useEffect(() => {
        if (playerData?.gangId) {
            const gangRef = doc(db, "gangs", playerData.gangId);
            const membersRef = collection(db, "gangs", playerData.gangId, "members");
            const unsubGang = onSnapshot(gangRef, (doc) => {
                setGangData(doc.exists() ? { id: doc.id, ...doc.data() } : null);
            });
            const unsubMembers = onSnapshot(membersRef, (snapshot) => {
                const memberList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setMembers(memberList);
            });
            return () => { unsubGang(); unsubMembers(); };
        } else {
            setGangData(null);
            setMembers([]);
        }
    }, [playerData?.gangId]);

    // Live countdown timers for building upgrades
    useEffect(() => {
        const interval = setInterval(() => {
            if (gangData?.buildings) {
                const newTimeRemaining = {};
                for (const buildingKey in gangData.buildings) {
                    const building = gangData.buildings[buildingKey];
                    if (building.lastUpgradedAt && building.level > 0) {
                        const cooldownHours = getUpgradeCooldownHoursClient ? getUpgradeCooldownHoursClient(building.level) : 24;
                        const unlockTime = building.lastUpgradedAt.toDate().getTime() + cooldownHours * 60 * 60 * 1000;
                        const now = Date.now();
                        if (now < unlockTime) {
                            const diff = unlockTime - now;
                            const d = Math.floor(diff / (1000 * 60 * 60 * 24));
                            const h = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                            const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                            newTimeRemaining[buildingKey] = `${d}d ${h}h ${m}m`;
                        }
                    }
                }
                setTimeRemaining(newTimeRemaining);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [gangData]);

    const handleActivatePass = async () => {
        if (!selectedGangPass) return setFeedback("Please select a Gang Pass to activate.");
        if (!window.confirm("Are you sure you want to activate this Gang Pass? It will be bound to your account and this action cannot be undone.")) return;
        setIsLoading(true);
        try {
            const res = await activateGangPassCallable({ assetId: selectedGangPass });
            setFeedback(res.data.message);
        } catch (e) {
            setFeedback(`Error: ${e.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateGang = async () => {
        if (!gangName || !gangTag) return setFeedback("Error: Gang Name and a 1-5 character Tag are required.");
        if (gangName.length > 24 || gangTag.length > 5) return setFeedback("Error: Gang name max 24 chars, tag max 5 chars.");
        if (!window.confirm(`Create gang "[${gangTag.toUpperCase()}] ${gangName}"? Cost: 1,000,000 WHATIF.`)) return;
        setIsLoading(true);
        try {
            const actions = [{
                account: tokenContracts['WHATIF'],
                name: 'transfer',
                authorization: [session.permissionLevel],
                data: { from: session.actor.toString(), to: MASTER_ACCOUNT_WAX_ID, quantity: '1000000.00000000 WHATIF', memo: 'WHATIF Dungeon For Riches - Gang Creation' },
            }];
            const txResult = await session.transact({ actions });
            setFeedback("Verifying transaction...");
            await new Promise(resolve => setTimeout(resolve, 5000));
            const result = await createGangCallable({ gangName, gangTag, txId: txResult.response.transaction_id });
            setFeedback(result.data.message);
            setGangName(''); setGangTag('');
        } catch (error) {
            setFeedback(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLeaveGang = async () => {
        if (!window.confirm("Are you sure you want to leave this gang?")) return;
        setIsLoading(true);
        try {
            const result = await leaveGangCallable();
            setFeedback(result.data.message);
        } catch (error) {
            setFeedback(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleJoinGang = async (gangId, gangName) => {
        if (!window.confirm(`Are you sure you want to join [${gangId}] ${gangName}?`)) return;
        setIsLoading(true);
        try {
            const result = await joinGangCallable({ gangId });
            setFeedback(result.data.message);
        } catch (error) {
            setFeedback(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeposit = async () => {
        const amount = parseFloat(depositAmount);
        if (isNaN(amount) || amount <= 0) return setFeedback("Error: Invalid amount.");
        if (!window.confirm(`Deposit ${amount.toLocaleString()} ${depositTokenSymbol} to the gang treasury?`)) return;
        setIsLoading(true);
        try {
            const quantity = `${amount.toFixed(tokenDecimals[depositTokenSymbol])} ${depositTokenSymbol}`;
            const actions = [{
                account: tokenContracts[depositTokenSymbol],
                name: 'transfer',
                authorization: [session.permissionLevel],
                data: { from: session.actor.toString(), to: MASTER_ACCOUNT_WAX_ID, quantity, memo: `GANG_DEPOSIT::${gangData.id}` },
            }];
            const txResult = await session.transact({ actions });
            setFeedback('Verifying deposit...');
            await new Promise(resolve => setTimeout(resolve, 5000));
            const result = await depositToGangTreasuryCallable({ gangId: gangData.id, tokenSymbol: depositTokenSymbol, amount, txId: txResult.response.transaction_id });
            setFeedback(result.data.message);
            setDepositAmount('');
        } catch (error) {
            setFeedback(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };


    if (playerData.gangId && gangData) {
        const isLeader = gangData.ownerId === session?.actor.toString();
        const myMemberData = members.find(m => m.id === session?.actor.toString());
        const strikesUsed = myMemberData?.raidStrikesUsed || 0;
        const strikesLeft = Math.max(0, 3 - strikesUsed);
        const raid = gangData.activeRaid;

        return (
            <main id="gang-dashboard-main" style={{ backgroundColor: 'rgba(0,0,0,0.7)', padding: '20px', borderRadius: '8px', maxWidth: '800px', margin: 'auto' }}>
                <header id="gang-header">
                    <h2 id="gang-name-title">[{gangData.gangTag}] {gangData.gangName}</h2>
                    <p id="gang-leadership-info">Leader: {gangData.ownerId} | Members: {gangData.memberCount}</p>
                </header>
                {feedback && <p style={{ color: feedback.startsWith('Error') ? 'red' : 'lightgreen', padding: '10px', background: '#333', borderRadius: '5px' }}>{feedback}</p>}


                <hr className="profile-separator" />

                {/* --- SHARED TREASURY --- */}
                <section id="shared-treasury-section">
                    <header>
                        <h3 id="shared-treasury-heading">Shared Treasury</h3>
                    </header>
                    <div style={{ border: '1px solid #007bff', padding: '15px', borderRadius: '5px', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', justifyContent: 'center', marginBottom: '20px', padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '5px' }}>
                            {gangData.treasury && Object.entries(gangData.treasury)
                                .filter(([token, balance]) => balance > 0)
                                .map(([token, balance]) => (
                                    <div key={token} style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{token}</div>
                                        <div style={{ fontSize: '14px', color: 'cyan' }}>{balance.toLocaleString(undefined, { maximumFractionDigits: 4 })}</div>
                                    </div>
                                ))
                            }
                            {gangData.treasury && Object.values(gangData.treasury).every(v => v === 0) && (
                                <p style={{ margin: 0, color: '#ccc' }}>The treasury is currently empty.</p>
                            )}
                        </div>
                        <div style={{ marginTop: '20px' }}>
                            <h4 id="deposit-funds-heading">Deposit Funds</h4>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <input id="deposit-amount-input" type="number" value={depositAmount} onChange={e => setDepositAmount(e.target.value)} placeholder="Amount" className="modern-input" style={{ flex: 2 }} />
                                <select id="deposit-token-select" value={depositTokenSymbol} onChange={e => setDepositTokenSymbol(e.target.value)} className="modern-input" style={{ flex: 1 }}>
                                    {['WAX', 'WHATIF', 'FIREMAN', 'PUZZL', 'WTFWUF', 'WTFBILL', 'WHATIFX', 'TESTERU', 'XMASZS'].map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                                <button id="deposit-funds-btn" onClick={handleDeposit} disabled={isLoading} className="modern-button" style={{ margin: 0 }}>Deposit</button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* --- GANG BUILDINGS --- */}
                <section id="gang-buildings-section">
                    <header>
                        <h3 id="gang-buildings-heading">Gang Buildings</h3>
                    </header>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        {Object.entries(buildingConfigClient).map(([buildingKey, config]) => {
                            const currentLevel = gangData.buildings?.[buildingKey]?.level ?? 0;
                            const currentBoost = gangData.gangBoosts?.[config.boostType] ?? 0;
                            const upgradeCost = getUpgradeCostClient(currentLevel, config.upgradeToken);
                            const remaining = timeRemaining[buildingKey];
                            const isOnCooldown = !!remaining;

                            return (
                                <article key={buildingKey} id={`building-card-${buildingKey}`} style={{ border: '1px solid #555', padding: '10px', borderRadius: '5px' }}>
                                    <h4 id={`building-name-${buildingKey}`} style={{ margin: '0 0 5px 0' }}>{config.name} (Level {currentLevel})</h4>
                                    <p style={{ fontSize: '12px', margin: '0 0 10px 0', color: '#ccc' }}>{config.description}</p>
                                    <p style={{ margin: '5px 0', color: 'cyan' }}>Current Boost: +{currentBoost}% {config.boostType}</p>
                                    <hr style={{ opacity: 0.2 }} />
                                    <p style={{ fontSize: '14px', margin: '10px 0' }}>Next Upgrade Cost: {upgradeCost.toLocaleString()} {config.upgradeToken}</p>

                                    {isLeader && (
                                        <button
                                            onClick={async () => {
                                                const currentTreasuryBalance = gangData.treasury?.[config.upgradeToken] || 0;
                                                if (currentTreasuryBalance < upgradeCost) {
                                                    setFeedback(`Error: Insufficient funds in treasury. Needs ${upgradeCost.toLocaleString()} ${config.upgradeToken}.`);
                                                    return;
                                                }
                                                if (!window.confirm(`This will use ${upgradeCost.toLocaleString()} ${config.upgradeToken} from the Shared Treasury to upgrade ${config.name}. Continue?`)) return;
                                                setIsLoading(true);
                                                try {
                                                    const result = await upgradeGangBuildingCallable({ gangId: gangData.id, buildingName: buildingKey });
                                                    setFeedback(result.data.message);
                                                } catch (error) {
                                                    setFeedback(`Error: ${error.message}`);
                                                } finally {
                                                    setIsLoading(false);
                                                }
                                            }}
                                            disabled={isLoading || isOnCooldown}
                                            className="modern-button"
                                            style={{ background: isOnCooldown ? '#555' : 'linear-gradient(145deg, #28a745, #218838)', cursor: isOnCooldown ? 'not-allowed' : 'pointer' }}
                                        >
                                            {isOnCooldown ? `Locked (${remaining})` : 'Upgrade'}
                                        </button>
                                    )}
                                </article>
                            );
                        })}
                    </div>
                </section>

                <hr className="profile-separator" />

                {/* --- MEMBERS & LEAVE --- */}
                <section id="gang-members-section">
                    <header>
                        <h3 id="gang-members-heading">Members ({gangData.memberCount} / 50)</h3>
                    </header>
                    <ul id="gang-members-list" style={{ listStyleType: 'none', padding: 0 }}>
                        {members.map(member => (
                            <li key={member.id} style={{ borderBottom: '1px solid #444', padding: '5px' }}>{member.id} - <b>{member.role}</b></li>
                        ))}
                    </ul>
                    <button id="leave-gang-btn" onClick={handleLeaveGang} disabled={isLoading} className="modern-button" style={{ background: 'darkred' }}>Leave Gang</button>
                </section>
            </main>
        );
    }

    return (
        <main id="gangs-page-container" className="gangs-container">

            {/* --- NEW: ACTIVATE GANG PASS PANEL --- */}
            {!playerData.gangPassAssetId && (
                <section id="activate-gang-pass-section" style={{ backgroundColor: 'rgba(0,0,0,0.7)', padding: '20px', borderRadius: '8px', gridColumn: '1 / -1', textAlign: 'center', border: '1px solid #ff9800', marginBottom: '20px' }}>
                    <header>
                        <h2 id="activate-gang-pass-heading" style={{ color: '#ff9800', marginTop: 0 }}>Activate Gang Pass</h2>
                    </header>
                    <p style={{ fontSize: '14px', color: '#ccc' }}>You need an active Gang Pass linked to your profile to create or join a gang.</p>

                    {gangPassNfts && gangPassNfts.length > 0 ? (
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', marginTop: '15px' }}>
                            <select className="modern-input" value={selectedGangPass} onChange={(e) => setSelectedGangPass(e.target.value)} style={{ flex: 1, maxWidth: '300px' }}>
                                <option value="">-- Select a Gang Pass --</option>
                                {gangPassNfts.map(nft => (
                                    <option key={nft.asset_id} value={nft.asset_id}>{nft.name} (#{nft.asset_id})</option>
                                ))}
                            </select>
                            <button id="activate-pass-btn" onClick={handleActivatePass} disabled={isLoading || !selectedGangPass} className="modern-button" style={{ margin: 0 }}>
                                {isLoading ? 'Activating...' : 'Activate Pass'}
                            </button>
                        </div>
                    ) : (
                        <p id="no-gang-pass-msg" style={{ color: 'orange', fontWeight: 'bold' }}>You do not own any Gang Pass NFTs. Please purchase one from the Store.</p>
                    )}
                </section>
            )}

            <section id="create-gang-section" style={{ backgroundColor: 'rgba(0,0,0,0.7)', padding: '20px', borderRadius: '8px' }}>
                <header>
                    <h3 id="create-gang-heading">Create a New Gang</h3>
                </header>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <input id="create-gang-name-input" type="text" value={gangName} onChange={e => setGangName(e.target.value)} placeholder="Gang Name (Max 24)" maxLength="24" className="modern-input" disabled={!playerData.gangPassAssetId} />
                    <input id="create-gang-tag-input" type="text" value={gangTag} onChange={e => setGangTag(e.target.value.toUpperCase())} placeholder="[TAG] (Max 5)" maxLength="5" className="modern-input" disabled={!playerData.gangPassAssetId} />
                    <button id="create-gang-btn" onClick={handleCreateGang} disabled={isLoading || !playerData.gangPassAssetId} className="modern-button">Create Gang</button>
                </div>
            </section>
            <section id="join-gang-section" style={{ backgroundColor: 'rgba(0,0,0,0.7)', padding: '20px', borderRadius: '8px' }}>
                <header>
                    <h3 id="join-gang-heading">Join an Existing Gang</h3>
                </header>
                <div id="join-gang-list-container" style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #444', padding: '10px', borderRadius: '5px' }}>
                    {allGangs.length > 0 ? allGangs.map(gang => (
                        <article key={gang.id} id={`join-gang-item-${gang.id}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', borderBottom: '1px solid #555' }}>
                            <div>
                                <p id={`join-gang-name-${gang.id}`} style={{ margin: 0, fontWeight: 'bold' }}>[{gang.gangTag}] {gang.gangName}</p>
                                <p id={`join-gang-members-${gang.id}`} style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#ccc' }}>Members: {gang.memberCount} / 50</p>
                            </div>
                            <button id={`join-gang-btn-${gang.id}`} onClick={() => handleJoinGang(gang.id, gang.gangName)} disabled={isLoading || !playerData.gangPassAssetId || gang.memberCount >= 50} className="modern-button" style={{ margin: 0 }}>Join</button>
                        </article>
                    )) : <p id="no-gangs-found-msg">No gangs found.</p>}
                </div>
            </section>
            {feedback && <p id="global-feedback-msg" style={{ color: feedback.startsWith('Error') ? 'red' : 'lightgreen', width: '100%', textAlign: 'center', gridColumn: '1 / -1' }}>{feedback}</p>}
        </main>
    );
}

export default GangsPage;